// Plan intent persistence (billing-signup.md §5.1).
//
// The website's pricing cards send `?plan=basic|premium&interval=monthly|annual`
// to /sign-in. Sign-up round-trips through email verification before the
// wizard can act on it, so the intent is stashed in localStorage the same way
// an invite redirect is (`inviteRedirect.ts`) and consumed by the wizard's plan
// step. The key is `synvya_*` so logout's sweep clears it.

const LS_PLAN_INTENT = 'synvya_plan_intent'

export type PlanChoice = 'basic' | 'premium'
export type BillingInterval = 'month' | 'year'

export interface PlanIntent {
  plan: PlanChoice
  interval: BillingInterval
}

/** Read a plan intent from query params. Unknown values mean "no intent". */
export function parsePlanIntent(params: URLSearchParams): PlanIntent | null {
  const plan = params.get('plan')
  if (plan !== 'basic' && plan !== 'premium') return null
  const raw = params.get('interval')
  const interval: BillingInterval = raw === 'monthly' || raw === 'month' ? 'month' : 'year'
  return { plan, interval }
}

export function storePlanIntent(intent: PlanIntent): void {
  try {
    localStorage.setItem(LS_PLAN_INTENT, JSON.stringify(intent))
  } catch {
    // localStorage unavailable — non-fatal.
  }
}

/** Read a stored intent without clearing it. */
export function peekPlanIntent(): PlanIntent | null {
  try {
    const raw = localStorage.getItem(LS_PLAN_INTENT)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PlanIntent>
    if (parsed.plan !== 'basic' && parsed.plan !== 'premium') return null
    return { plan: parsed.plan, interval: parsed.interval === 'month' ? 'month' : 'year' }
  } catch {
    return null
  }
}

/** Read and clear the stored intent, or null if none. */
export function consumePlanIntent(): PlanIntent | null {
  const intent = peekPlanIntent()
  try {
    localStorage.removeItem(LS_PLAN_INTENT)
  } catch {
    // ignore
  }
  return intent
}
